// pages/Profile.tsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Shield,
  CheckCircle,
  X,
  ArrowRight,
  Calendar,
} from "lucide-react";

// Import child components
import PersonalInformation from "../components/profile/PersonalInformation";
import AddressInformation from "../components/profile/AddressInformation";
import SSNVerification from "../components/profile/SSNVerification";
import SSNVerificationModal from "../components/profile/SSNVerificationModal";
import SkillsAndPreferences from "../components/profile/SkillsAndPreferences";
import ProfileHeader from "../components/profile/VerificationBadges";
import {
  useFetchUserDetails,
  useUpdateUser,
  useVerifySSN,
} from "../lib/api/authOnboarding";
import { getDecodedJwt } from "../lib/auth";
import ProfileSkeleton from "../components/profile/ProfileSkeleton";
import {
  ILanguage,
  SSNVerificationPayload,
  WeeklyAvailability,
} from "../lib/types/authOnboarding";

interface SkillsAndPreferencesData {
  skills: string[];
  languages: ILanguage[];
  preferredCategories: string[];
  weeklyAvailability: WeeklyAvailability;
}

interface PersonalInfo {
  firstName: string;
  lastName: string;
  middleName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
}

interface AddressInfo {
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

interface Verification {
  emailVerified: boolean;
  addressVerified: boolean;
  ssnVerified: boolean;
  ssnVerificationStatus: string;
}

interface PendingVerification {
  key: string;
  label: string;
  description: string;
  icon: React.ElementType;
  action?: () => void;
  isPrerequisite?: boolean;
}

const Profile: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const user = getDecodedJwt();
  const {
    data: userDetails,
    isLoading,
    refetch,
  } = useFetchUserDetails(user?.id);
  const { mutateAsync: updateUser, isPending } = useUpdateUser();
  const { mutateAsync: verifySSN, isPending: isSSNVerifyPending } =
    useVerifySSN();

  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [addressInfo, setAddressInfo] = useState<AddressInfo | null>(null);
  const [verification, setVerification] = useState<Verification | null>(null);
  const [skillsAndPreferences, setSkillsAndPreferences] =
    useState<SkillsAndPreferencesData | null>(null);
  const [showSSNModal, setShowSSNModal] = useState(false);
  const [showVerificationPromptModal, setShowVerificationPromptModal] =
    useState(false);
  const [hasShownPrompt, setHasShownPrompt] = useState(false);

  // Check if DOB is filled
  const isDobFilled = Boolean(personalInfo?.dateOfBirth);

  // Populate state when userDetails is fetched
  useEffect(() => {
    if (userDetails) {
      const data = userDetails;

      setPersonalInfo({
        firstName: data.firstname || "",
        lastName: data.lastname || "",
        middleName: data.middlename || "",
        phoneNumber: data.phoneNumber || "",
        email: data.email || "",
        dateOfBirth: data.dateOfBirth || "",
      });

      setAddressInfo({
        address: data.address?.street || "",
        city: data.address?.city || "",
        state: data.address?.state || "",
        country: data.address?.country || "",
        zipCode: data.address?.zipCode || "",
      });

      setVerification({
        emailVerified: data.verification?.emailVerified || false,
        addressVerified: data.verification?.addressVerified || false,
        ssnVerified: data.verification?.ssnVerified || false,
        ssnVerificationStatus:
          data.verification?.ssnVerificationStatus || "not_started",
      });

      setSkillsAndPreferences({
        skills: data.skillsAndPreferences?.skills || [],
        languages: data.skillsAndPreferences?.languages || [],
        preferredCategories:
          data.skillsAndPreferences?.preferredCategories || [],
        weeklyAvailability: data.skillsAndPreferences?.weeklyAvailability || "",
      });
    }
  }, [userDetails]);

  // Show verification prompt modal if user has pending verifications (DOB or SSN only)
  useEffect(() => {
    if (verification && personalInfo && !hasShownPrompt) {
      const hasPendingVerification =
        !personalInfo.dateOfBirth || !verification.ssnVerified;

      // Show modal if redirected from login or has pending verifications
      const fromLogin = location.state?.showVerificationPrompt;

      if (hasPendingVerification && (fromLogin || !hasShownPrompt)) {
        setShowVerificationPromptModal(true);
        setHasShownPrompt(true);
      }
    }
  }, [verification, personalInfo, location.state, hasShownPrompt]);

  const handlePersonalInfoSave = async (
    data: PersonalInfo
  ): Promise<boolean> => {
    try {
      await updateUser({
        id: user?.id as string,
        payload: {
          firstname: data.firstName,
          lastname: data.lastName,
          middlename: data.middleName,
          dateOfBirth: data.dateOfBirth,
          ...(data.phoneNumber ? { phoneNumber: data.phoneNumber } : {}),
        },
      });
      refetch();
      return true;
    } catch (error) {
      console.error("Error updating personal info:", error);
      return false;
    }
  };

  const handleAddressInfoSave = async (data: AddressInfo): Promise<boolean> => {
    try {
      await updateUser({
        id: user?.id as string,
        payload: {
          address: {
            street: data.address,
            city: data.city,
            state: data.state,
            country: data.country,
            zipCode: data.zipCode,
          },
        },
      });
      refetch();
      return true;
    } catch (error) {
      console.error("Error updating address info:", error);
      return false;
    }
  };

  const handleSkillsAndPreferencesSave = async (
    data: SkillsAndPreferencesData
  ): Promise<boolean> => {
    try {
      await updateUser({
        id: user?.id as string,
        payload: {
          skillsAndPreferences: {
            skills: data.skills,
            languages: data.languages,
            preferredCategories: data.preferredCategories,
            weeklyAvailability: data.weeklyAvailability,
          },
        },
      });
      refetch();
      return true;
    } catch (error) {
      console.error("Error updating skills and preferences:", error);
      return false;
    }
  };

  const handleSSNVerificationComplete = async (
    data: SSNVerificationPayload
  ) => {
    try {
      await verifySSN({
        id: user?.id as string,
        payload: {
          ssn: data.ssn,
        },
      });
      refetch();
      setShowSSNModal(false);
      return true;
    } catch (error) {
      console.error("Error updating SSN verification:", error);
      return false;
    }
  };

  // Get pending verifications list (DOB and SSN only)
  const getPendingVerifications = (): PendingVerification[] => {
    if (!verification || !personalInfo) return [];

    const pending: PendingVerification[] = [];

    // DOB is a prerequisite for SSN verification
    if (!personalInfo.dateOfBirth) {
      pending.push({
        key: "dob",
        label: "Date of Birth",
        description:
          "Required for SSN verification. Please add your date of birth first.",
        icon: Calendar,
        isPrerequisite: true,
        action: () => {
          setShowVerificationPromptModal(false);
          document
            .getElementById("personal-info-section")
            ?.scrollIntoView({ behavior: "smooth" });
        },
      });
    }

    if (verification.ssnVerificationStatus === "not_started") {
      pending.push({
        key: "ssn",
        label: "SSN Verification",
        description: !personalInfo.dateOfBirth
          ? "Complete your date of birth first to enable SSN verification"
          : "Verify your Social Security Number for payment eligibility",
        icon: Shield,
        action: () => {
          if (!personalInfo.dateOfBirth) {
            setShowVerificationPromptModal(false);
            document
              .getElementById("personal-info-section")
              ?.scrollIntoView({ behavior: "smooth" });
          } else {
            setShowVerificationPromptModal(false);
            setShowSSNModal(true);
          }
        },
      });
    }

    return pending;
  };

  const pendingVerifications = getPendingVerifications();
  const totalRequirements = 2; // DOB + SSN
  const completedCount =
    (isDobFilled ? 1 : 0) + (verification?.ssnVerified ? 1 : 0);

  // Verification Prompt Modal
  const VerificationPromptModal = () => {
    if (!showVerificationPromptModal || pendingVerifications.length === 0)
      return null;

    return createPortal(
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowVerificationPromptModal(false)}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden z-10 max-h-[80vh] flex flex-col">
          {/* Header - Fixed */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white flex-shrink-0">
            <button
              onClick={() => setShowVerificationPromptModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-full">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Complete Your Profile</h2>
                <p className="text-white/90 text-sm mt-1">
                  Verification required to be eligible for job assignments
                </p>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Progress */}
            <div className="px-6 pt-5">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Verification Progress</span>
                <span className="font-semibold text-gray-900">
                  {completedCount}/{totalRequirements} Complete
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${(completedCount / totalRequirements) * 100}%`,
                  }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                Please complete the following requirements to start receiving
                job assignments and earn money:
              </p>

              {/* DOB Warning if missing */}
              {!isDobFilled && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Note:</span> Date of Birth
                    is required before you can complete SSN verification.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {pendingVerifications.map((item) => (
                  <button
                    key={item.key}
                    onClick={item.action}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all group text-left ${
                      item.isPrerequisite
                        ? "bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300"
                        : "bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`p-2.5 rounded-lg transition-colors ${
                        item.isPrerequisite
                          ? "bg-blue-100 text-blue-600 group-hover:bg-blue-200"
                          : "bg-amber-100 text-amber-600 group-hover:bg-amber-200"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">
                          {item.label}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {item.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}

                {/* Show completed items */}
                {isDobFilled && (
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="p-2.5 bg-green-100 rounded-lg text-green-600">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800">
                        Date of Birth
                      </h4>
                      <p className="text-sm text-green-600">Completed</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                )}

                {verification?.ssnVerified && (
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="p-2.5 bg-green-100 rounded-lg text-green-600">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800">
                        SSN Verification
                      </h4>
                      <p className="text-sm text-green-600">Completed</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={() => setShowVerificationPromptModal(false)}
              className="w-full py-3 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Show skeleton while loading
  if (
    isLoading ||
    !personalInfo ||
    !addressInfo ||
    !verification ||
    !skillsAndPreferences
  ) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Verification Alert Banner - Only show if DOB or SSN is incomplete */}
        {pendingVerifications.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800">
                  Profile Incomplete
                </h3>
                <p className="text-sm text-amber-700">
                  Complete {pendingVerifications.length} remaining requirement
                  {pendingVerifications.length > 1 ? "s" : ""} to be eligible
                  for job assignments.
                  {!isDobFilled && (
                    <span className="block mt-1 text-amber-600">
                      Start by adding your Date of Birth.
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowVerificationPromptModal(true)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            >
              Complete Now
            </button>
          </div>
        )}

        {/* Verification Badges */}
        <ProfileHeader verification={verification} isDobFilled={isDobFilled} />

        {/* Main Content Grid */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal & Address Info */}
          <div className="lg:col-span-2 space-y-6">
            <div id="personal-info-section">
              <PersonalInformation
                data={personalInfo}
                onSave={handlePersonalInfoSave}
                isPending={isPending}
              />
            </div>
            <div id="address-section">
              <AddressInformation
                data={addressInfo}
                onSave={handleAddressInfoSave}
                isPending={isPending}
              />
            </div>
            {/* Skills & Preferences */}
            <SkillsAndPreferences
              data={skillsAndPreferences}
              onSave={handleSkillsAndPreferencesSave}
              isPending={isPending}
            />
          </div>

          {/* Right Column - SSN Verification Only */}
          <div className="space-y-6">
            <SSNVerification
              status={verification.ssnVerificationStatus}
              onStartVerification={() => setShowSSNModal(true)}
            />
          </div>
        </div>
      </div>

      {/* Verification Prompt Modal */}
      <VerificationPromptModal />

      {/* SSN Verification Modal */}
      {showSSNModal && (
        <SSNVerificationModal
          onClose={() => setShowSSNModal(false)}
          onComplete={handleSSNVerificationComplete}
          isPending={isSSNVerifyPending}
        />
      )}
    </div>
  );
};

export default Profile;
